import React, { useState, useEffect, useContext } from "react";

import { GlobalContext } from "@/contexts/GlobalContext";

import ScrollButton from "@/components/ScrollButton";
import ProgressDisplay from "@/components/ProgressDisplay";
import Notification from "@/components/Notification.component";

import useFetch from "@/hooks/useFetchData";

import SiteHead from "@/layouts/Head";
import Layout from "@/layouts/Layout";

import { FetchOptions } from "@/types/HTTP.types";

import styles from "@/styles/contact.module.scss";

interface FileProps {
    fieldname?: string;
    originalname?: string;
    encoding?: string;
    mimetype?: string;
    destination?: string;
    filename?: string;
    path?: string;
    size?: number;
};

interface Files {
    [fieldname: string]: FileProps[];
};

interface FormInputs {
    fullname?: string | null;
    email?: string | null;
    subject?: string | null;
    message?: string | null;
    reg?: string | null;
    docFile?: string | null;
    imageFile?: string | null;
    image2File?: string | null;
    image3File?: string | null;
    image4File?: string | null;
};

const emailReg = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
const subjects: { name: string; value: string; }[] = [
    {
        name: "Sales",
        value: "sales"
    },
    {
        name: "Returns",
        value: "returns"
    },
    {
        name: "Delivery",
        value: "delivery"
    }
];

const alphaChars = Array.from(Array(26)).map((_, i: number) => i + 65);
const alphabet = alphaChars.map((x) => String.fromCharCode(x));
const numarals: string[] = Array.from(Array(10).keys()).toString().split(",");
const chars = [...alphabet, ...numarals];

const ContactUs = () => {
    const { globalState: { apiUrl } } = useContext(GlobalContext);
    const { useFetchData } = useFetch();

    const [formInputs, setFormInputs] = useState<FormInputs | null>(null);
    const [errors, setErrors] = useState<FormInputs | null>(null);
    const [captcha, setCaptcha] = useState<string | null>(null);
    const [captchaInput, setCaptchaInput] = useState<string | null>(null);
    const [displayMessage, setDisplayMessage] = useState<string | null>(null);

    const [submitForm, setSubmitForm] = useState<boolean>(false);
    const [canSubmit, setCanSubmit] = useState<boolean>(false);
    const [checkCaptcha, setCheckCaptcha] = useState<boolean>(false);
    const [expandFields, setExpandFields] = useState<boolean>(false);
    const [isInProgress, setIsInProgress] = useState<boolean>(false);

    const [submitFileUpload, setSubmitFileUpload] = useState<boolean>(false);
    const [fieldName, setFieldName] = useState<string>();
    const [selFile, setSelFile] = useState<File>();

    useEffect(() => {
        generateCaptchCode(chars);
    }, []);

    useEffect(() => {
        if (submitForm) {
            sendMessage();
            setSubmitForm(false);
        }
    }, [submitForm]);

    useEffect(() => {
        if (checkCaptcha && captcha && captchaInput) {
            validateCaptcha(captcha, captchaInput);
            setCheckCaptcha(false);
        }
    }, [checkCaptcha, captcha, captchaInput]);

    useEffect(() => {
        if (submitFileUpload) {
            uploadFile(fieldName!, selFile!);
            setSubmitFileUpload(false);
        }
    }, [submitFileUpload]);

    /* ------------------------------------ API FUNCTIONS ------------------------------------ */

    const sendMessage = async () => {
        const url: string = apiUrl + "/messages/contact";
        const options: FetchOptions = {
            method: "POST",
            body: JSON.stringify(formInputs)
        };

        try {
            const result = await useFetchData(url, options);

            setDisplayMessage(null);
            setIsInProgress(false);

            if (!result.success) {
                return false;
            }

            setFormInputs(null);
            setCaptchaInput(null);
            generateCaptchCode(chars);
            setCanSubmit(false);
        } catch (error) {
            console.error(error);
        }
    };

    const uploadFile = async (name: string, uploadFile: File) => {
        const data = new FormData();
        data.append(name, uploadFile);

        const url: string = apiUrl + "/messages/upload";
        const options: FetchOptions = {
            method: "POST",
            headers: {
                "Content-Type": "multipart/form-data"
            },
            body: data
        };

        try {
            const result = await useFetchData<Files>(url, options);

            setDisplayMessage(null);
            setIsInProgress(false);

            if (!result.success) {
                return false;
            }

            const files = result.data;
            Object.keys(files).forEach(key => {
                const file = files[key][0];
                setFormInputs({...formInputs, [file.fieldname as string + "File"]: file.filename});
            });
        } catch (error) {
            console.log(error);
        }
    };

    const generateCaptchCode = (chlist: string[]) => {
        const length = chlist.length;
        let codeString: string = "";

        for (let i: number = 0; i < length; i++) {
            const randomChar = Math.floor(Math.random() * length);
            codeString += chlist[randomChar];
            if (i === 5) break;
        }

        setCaptcha(codeString);
    };

    const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = event.currentTarget;

        if (name !== "subject" && value === "") {
            return setFormInputs({...formInputs, [name]: null});    
        }

        if (name === "subject" && value === "null") {
            return setFormInputs({...formInputs, [name]: null});
        }

        if (name === "reg") {
            return setFormInputs({...formInputs, [name]: value.toUpperCase()});
        }

        setFormInputs({...formInputs, [name]: value});
    };

    const populateDefaultErrors = () => {
        setErrors({
            email: "Please enter your e-mail",
            fullname: "Please enter your name",
            subject: "Please select a subject",
            message: "Please enter a message"
        });
    };

    const checkFormInputs = (): boolean => {
        let pass: boolean = true;
        const formErrors: FormInputs = {};

        if (!formInputs) {
            populateDefaultErrors();
            return false;
        }

        if (!formInputs?.fullname) { formErrors.fullname = "Please enter your name"; pass = false; }

        if (!formInputs?.email) { formErrors.email = "Please enter your e-mail"; pass = false; }

        if (!formInputs?.subject) { formErrors.subject = "Please select a subject"; pass = false; }

        if (!formInputs?.message) { formErrors.message = "Please enter a message"; pass = false; }

        if (formInputs.email && !emailReg.test(formInputs.email)) { formErrors.email = "Please enter a valid e-mail"; pass = false; }

        if (Object.keys(formErrors).length > 0) {
            setErrors(formErrors);
        }

        return pass;
    };

    const handleChangeCaptcha = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.currentTarget;

        if (value === "") {
            return setCaptchaInput(null);
        }

        if (value.length === 6) {
            setCheckCaptcha(true);
        }

        setCaptchaInput(value.toUpperCase());
    };

    const validateCaptcha = (cap: string, input: string) => {
        if (cap === input) {
            return setCanSubmit(true);
        }
    };

    const handleRefreshCaptcha = () => {
        generateCaptchCode(chars);
    }

    const handleSubmitForm = (event: React.FormEvent) => {
        event.preventDefault();

        const formChecked = checkFormInputs();

        if (formChecked) {
            setIsInProgress(true);
            setDisplayMessage("Sending message...");
            setSubmitForm(true);
        }
    };

    const handleFileSubmit = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, files } = event.currentTarget;
        setFieldName(name);
        setSelFile(files![0]);
        setSubmitFileUpload(true);
        setDisplayMessage("Uploading file...");
        setIsInProgress(true);
    };

    const handleClearField = (event: React.MouseEvent<HTMLButtonElement>) => {
        const { name } = event.currentTarget;

        setFormInputs({...formInputs, [name]: null });
    };

    const toggleAttachmentFields = () => {
        setExpandFields(!expandFields);
    };

    const contactForm = (
        <form onSubmit={handleSubmitForm} className={styles["form"]}>
            <label 
                className={styles["form-label"]} 
                htmlFor="form-fullname"
            >Name: <span className={styles["form-required"]} >*</span></label>
            {errors && errors.fullname && <p className={styles["form-error-text"]}>{errors.fullname}</p>}
            <input 
                // className={styles[`form-input ${errors?.fullname ? "form-error" : ""}`]} 
                className={`${styles[`form-input`]} ${errors?.fullname ? "form-error" : ""}`} 
                id="form-fullname" 
                onChange={handleChangeInput} 
                type="text" 
                name="fullname"
                autoComplete="off"
                value={formInputs?.fullname || ""} 
            />

            <label 
                className={styles["form-label"]} 
                htmlFor="form-email"
            >E-Mail Address: <span className={styles["form-required"]} >*</span></label>
            {errors && errors.email && <p className={styles["form-error-text"]}>{errors.email}</p>}
            <input 
                className={`${styles[`form-input`]} ${errors?.email ? "form-error" : ""}`} 
                id="form-email" 
                onChange={handleChangeInput} 
                type="text" 
                name="email"
                autoComplete="off"
                value={formInputs?.email || ""} 
            />

            <label 
                className={styles["form-label"]} 
                htmlFor="form-subject"
            >Subject: <span className={styles["form-required"]} >*</span></label>
            {errors && errors.subject && <p className={styles["form-error-text"]}>{errors.subject}</p>}
            <select 
                className={`${styles[`form-input`]} ${styles[`form-select`]} ${errors?.subject ? "form-error" : ""}`} 
                id="form-subject" 
                onChange={handleChangeInput} 
                name="subject" 
                value={formInputs?.subject || ""} 
            >
                <option value="null">--- Select Subject ---</option>
                {subjects.map(sub => (
                    <option key={sub.value} value={sub.value}>{sub.name}</option>
                ))}
            </select>

            <label className={styles["form-label"]} htmlFor="form-reg">Vehicle Registration:</label>
            <input 
                className={`${styles["form-input"]} ${styles["form-reg"]}`} 
                id="form-reg" 
                onChange={handleChangeInput} 
                type="text" 
                name="reg" 
                autoComplete="off"
                value={formInputs?.reg || ""} 
            />

            <label 
                className={styles["form-label"]} 
                htmlFor="form-message"
            >Message: <span className={styles["form-required"]} >*</span></label>
            {errors && errors.message && <p className={styles["form-error-text"]}>{errors.message}</p>}
            <textarea 
                className={`${styles[`form-text`]} ${errors?.message ? "form-error" : ""}`} 
                id="form-message" 
                onChange={handleChangeInput} 
                name="message" 
                rows={10} 
                value={formInputs?.message || ""} 
            />

            <span onClick={toggleAttachmentFields} className={styles["form-expand-btn"]}>Attach Files</span>
            <div className={styles[`form-attachment-fields${expandFields ? "--visible" : ""}`]}>
                <div className={styles["doc-inputs"]}>
                    <p className={styles["doc-inputs-title"]}>Upload a PDF Document</p>
                    <label 
                        className={`${styles[`doc-inputs-label${formInputs?.docFile ? "--up" : ""}`]}`} 
                        htmlFor="doc"
                    >{!formInputs?.docFile ? "Upload" : "Clear"}</label>
                    <input 
                        type="text" 
                        readOnly 
                        name="docFile" 
                        hidden 
                        value={formInputs?.docFile || ""} 
                    />
                    {!formInputs?.docFile ? <input 
                            hidden 
                            onChange={handleFileSubmit} 
                            type="file" 
                            id="doc" 
                            name="doc" 
                            accept="application/pdf" 
                        /> : <button onClick={handleClearField} hidden name="docFile" id="doc" />}
                </div>

                <div className={styles["image-inputs"]}>
                    <p className={styles["image-inputs-title"]}>Upload Image(s)</p>
                    <span className={styles["image-inputs-note"]}>JPEG/JPG files only</span>

                    <label 
                        className={`${styles[`image-inputs-label${formInputs?.imageFile ? "--up" : ""}`]}`} 
                        htmlFor="image"
                    >{!formInputs?.imageFile ? "Upload" : "Clear"}</label>
                    <input 
                        type="text" 
                        readOnly 
                        name="imageFile" 
                        hidden 
                        value={formInputs?.imageFile || ""} 
                    />
                    {!formInputs?.imageFile ? <input 
                            hidden 
                            onChange={handleFileSubmit} 
                            type="file" 
                            id="image" 
                            name="image" 
                            accept="image/jpeg" 
                        /> : <button onClick={handleClearField} hidden name="imageFile" id="image" />}

                    <label 
                        className={`${styles[`image-inputs-label${formInputs?.image2File ? "--up" : ""}`]}`} 
                        htmlFor="image2"
                    >{!formInputs?.image2File ? "Upload" : "Clear"}</label>
                    <input 
                        type="text" 
                        readOnly 
                        name="image2File" 
                        hidden 
                        value={formInputs?.image2File || ""} 
                    />
                    {!formInputs?.image2File ? <input 
                            hidden 
                            onChange={handleFileSubmit} 
                            type="file" 
                            id="image2" 
                            name="image2" 
                            accept="image/jpeg" 
                        /> : <button onClick={handleClearField} hidden name="image2File" id="image2" />}

                    <label 
                        className={`${styles[`image-inputs-label${formInputs?.image3File ? "--up" : ""}`]}`} 
                        htmlFor="image3"
                    >{!formInputs?.image3File ? "Upload" : "Clear"}</label>
                    <input 
                        type="text" 
                        readOnly 
                        name="image3File" 
                        hidden 
                        value={formInputs?.image3File || ""} 
                    />
                    {!formInputs?.image3File ? <input 
                            hidden 
                            onChange={handleFileSubmit} 
                            type="file" 
                            id="image3" 
                            name="image3" 
                            accept="image/jpeg" 
                        /> : <button onClick={handleClearField} hidden name="image3File" id="image3" />}

                    <label 
                        className={`${styles[`image-inputs-label${formInputs?.image4File ? "--up" : ""}`]}`} 
                        htmlFor="image4"
                    >{!formInputs?.image4File ? "Upload" : "Clear"}</label>
                    <input type="text" readOnly name="image4File" hidden value={formInputs?.image4File || ""} />
                    {!formInputs?.image4File ? <input 
                            hidden 
                            onChange={handleFileSubmit} 
                            type="file" 
                            id="image4" 
                            name="image4" 
                            accept="image/jpeg" 
                        /> : <button onClick={handleClearField} hidden name="image4File" id="image4" />}

                </div>
            </div>
            <div className={styles["form-captcha-box"]}>
                <div className={styles["form-captcha-ref"]}>
                    <input 
                        onClick={handleRefreshCaptcha} 
                        className={styles["form-captcha"]} 
                        type="text" 
                        id="captcha"
                        value={captcha || ""} 
                        readOnly 
                    />
                    <button 
                        disabled={true} 
                        data-desc="Click on the CAPTCHA to refresh" 
                        className={styles["form-ref-notify"]} 
                    >&#63;</button>
                </div>

                <input 
                    disabled={canSubmit} 
                    onChange={handleChangeCaptcha} 
                    value={captchaInput || ""} 
                    type="text"
                    autoComplete="off"
                    id="captcha-input"
                    className={styles["form-captcha-input"]} 
                    placeholder="Enter the code above" 
                />
            </div>
            <input 
                disabled={!canSubmit ? true : false} 
                className={styles[`form-submit${!canSubmit ? "--disabled" : ""}`]} 
                type="submit" 
                value="Send" 
            />
        </form>
    );

    return (
        <React.Fragment>
            <SiteHead 
                title="Contact Us" 
                bc="contact" 
                desc="Contact Us" 
                keywords="contact, doctor, car, parts, dr" 
            />
            {isInProgress && <ProgressDisplay message={displayMessage} />}
            <Notification timeout={2000} />

            <Layout>
                <div className={styles["container"]}>
                    <h1>Let us know how we can help</h1>
                    {contactForm}
                </div>
            </Layout>

            <ScrollButton scrollLength={300} />
        </React.Fragment>
    );
};

export default ContactUs;
